import { useRef, useEffect, useCallback } from "react";
import textToSpeech from "../utils/textToSpeech";

/**
 * useAlexaMode — Alexa-style continuous voice session for Talent Pulse.
 *
 * Behaviour:
 *  - Mic stays open whenever voice session is active.
 *  - If user speaks and pauses for 3 seconds → auto-send query.
 *  - If user says "stop" → stop readout (TTS / Azure VOP) + streaming.
 *  - After readout ends → re-open mic; idle-close after 10 seconds of silence.
 *
 * @param {object} params
 * @param {string}   params.message                 – current message text
 * @param {boolean}  params.isLoading               – true while streaming
 * @param {Function} params.handleSendMessage        – send function (customContent, enableWebSearch, skipConsent, retainVoiceOutput)
 * @param {Function} params.stopStreaming            – abort streaming
 * @param {React.MutableRefObject} params.chatPromptContainerRef – ref to ChatPromptContainer
 * @param {Function} params.setKeepListening         – setState for keepListeningWhileLoading
 */
const useAlexaMode = ({
    message,
    isLoading,
    handleSendMessage,
    stopStreaming,
    chatPromptContainerRef,
    setKeepListening,
    alexaActiveRef: externalAlexaActiveRef,
}) => {
    // Refs to avoid stale closures inside timeouts / callbacks
    const internalAlexaActiveRef = useRef(false);
    const alexaActiveRef     = externalAlexaActiveRef || internalAlexaActiveRef; // true while in a voice session
    const messageRef         = useRef(message);
    const isLoadingRef       = useRef(isLoading);
    const handleSendRef      = useRef(handleSendMessage);
    const stopStreamingRef   = useRef(stopStreaming);
    const idleTimerRef       = useRef(null);

    useEffect(() => { messageRef.current  = message;         }, [message]);
    useEffect(() => { isLoadingRef.current = isLoading;      }, [isLoading]);
    useEffect(() => { handleSendRef.current = handleSendMessage; }, [handleSendMessage]);
    useEffect(() => { stopStreamingRef.current = stopStreaming;  }, [stopStreaming]);

    // ── helpers ────────────────────────────────────────────────────────────

    const clearIdleTimer = useCallback(() => {
        if (idleTimerRef.current) {
            clearTimeout(idleTimerRef.current);
            idleTimerRef.current = null;
        }
    }, []);

    /** Stop TTS and Azure VOP readout only — does NOT abort the streaming API call. */
    const stopTts = useCallback(() => {
        textToSpeech.stop();
        // forceAll:true stops ALL Azure VOP and Streaming VOP instances
        window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { forceAll: true } }));
        window.dispatchEvent(new CustomEvent("streamingVopStop", { detail: { forceAll: true } }));
    }, []);

    /** Stop TTS/Azure VOP AND abort the streaming API call (used for voice keywords and new-voice-start). */
    const stopAll = useCallback(() => {
        stopTts();
        // Always call stopStreaming — it is idempotent and also aborts the completion API
        stopStreamingRef.current?.();
    }, [stopTts]);

    // ── Auto-send after 3-second speech pause ─────────────────────────────

    useEffect(() => {
        if (!alexaActiveRef.current || !message?.trim()) return;
        
        // As soon as the user starts speaking, clear the idle timer so we don't 
        // accidentally close the mic while they are talking or waiting for the response.
        clearIdleTimer();

        const timer = setTimeout(() => {
            // Double-check message is still non-empty (guards against race where
            // setMessage("") fires between timer-set and timer-fire)
            const currentMsg = messageRef.current?.trim();
            if (alexaActiveRef.current && currentMsg) {
                // retainVoiceOutput=true so response auto-plays TTS
                handleSendRef.current?.(currentMsg, false, false, true);
            }
        }, 3000);
        return () => clearTimeout(timer);
        // Only re-run when message text changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    // ── Callbacks returned to TalentPulse360 ──────────────────────────────

    /** User manually taps mic. */
    const onVoiceStart = useCallback(() => {
        stopAll();           // stop any current readout + streaming before mic opens
        alexaActiveRef.current = true;
        clearIdleTimer();
    }, [stopAll, clearIdleTimer, alexaActiveRef]);

    /** User manually stops mic (Stop button clicked).
     *  Silences voice output (TTS / Azure VOP) but does NOT abort the streaming
     *  API response — the LLM can keep generating; the user just opted out of voice.
     */
    const onVoiceStop = useCallback(() => {
        alexaActiveRef.current = false;
        stopTts();            // stop TTS / Azure VOP only — streaming continues
        setKeepListening(false);
        clearIdleTimer();
    }, [stopTts, setKeepListening, clearIdleTimer, alexaActiveRef]);

    /**
     * "stop" / "cancel" keyword detected in speech.
     * Halts readout + streaming, but KEEPS the mic open for the next query.
     */
    const onVoiceKeyword = useCallback(() => {
        stopAll();
        setKeepListening(false);
        clearIdleTimer();
    }, [stopAll, setKeepListening, clearIdleTimer]);

    /**
     * Azure VOP / TTS started playing the response.
     * Keep mic open in background so user can say "stop".
     */
    const onVoiceOutputStart = useCallback(() => {
        if (!alexaActiveRef.current) return;
        setKeepListening(true);
        clearIdleTimer();
        // Force-restart mic for a fresh background-mode connection
        chatPromptContainerRef.current?.stopListening?.();
        setTimeout(() => {
            if (alexaActiveRef.current) {
                chatPromptContainerRef.current?.startListening?.();
            }
        }, 150);
    }, [setKeepListening, clearIdleTimer, chatPromptContainerRef, alexaActiveRef]);

    /**
     * Azure VOP / TTS finished reading out.
     * Re-open mic; idle-close after 10 seconds if user says nothing.
     */
    const onVoiceOutputEnd = useCallback(() => {
        if (!alexaActiveRef.current) return;
        setKeepListening(false);      // exit background/keyword mode
        clearIdleTimer();
        // Stop whatever mic session was running in background mode, then
        // open a fresh foreground session (with sound prompt).
        chatPromptContainerRef.current?.stopListening?.();
        setTimeout(() => {
            if (!alexaActiveRef.current) return;
            chatPromptContainerRef.current?.startVoice?.();
        }, 200);
        // Idle timeout — if nothing spoken in 15 sec, close mic
        idleTimerRef.current = setTimeout(() => {
            if (!messageRef.current?.trim() && !isLoadingRef.current) {
                alexaActiveRef.current = false;
                setKeepListening(false);
                chatPromptContainerRef.current?.stopVoice?.();
            }
        }, 15000);
    }, [setKeepListening, clearIdleTimer, chatPromptContainerRef, alexaActiveRef]);

    return {
        onVoiceStart,
        onVoiceStop,
        onVoiceKeyword,
        onVoiceOutputStart,
        onVoiceOutputEnd,
        alexaActiveRef,
        isAlexaActive: () => alexaActiveRef.current,
    };
};

export default useAlexaMode;
